import { cs } from "../../utils/currency";
import { getDeliveryAddress } from "../../utils/deliveryAddress";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, Undo2, RefreshCw } from "lucide-react";
import api from "../../services/api";
import { getTokenInfo } from "../../utils/auth";
import { RefundOrderModal, CompensateOrderModal } from "../../components/OrderActionModals";

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
  tax?: number;
  discountAmount?: number;
  itemstatus?: string;
  cancelledQuantity?: number;
  requestedCustomization?: string;
  dealName?: string;
  customOptions?: { label: string; price: number; qty: number; status?: string }[];
}

interface OrderDetail {
  _id: string;
  orderNumber: string;
  invoiceNumber?: string;
  items: OrderItem[];
  totalAmount: number;
  phoneNumber?: string;
  tableToken?: number;
  orderBy?: string;
  orderSource?: string;
  employeeName?: string;
  choice?: string;
  payChoice?: string;
  orderStatus?: string;
  statusHistory?: { status: string; updatedAt: string }[];
  scheduledAt?: string;
  isScheduled?: boolean;
  createdAt: string;
  deliveryStatus?: string;
  deliveryLiveMapUrl?: string;
  location?: { name?: string } | null;
  metadata?: Record<string, unknown>;
  bonusAmountUsed?: number;
  partialRefunds?: { amount: number; reason?: string; createdAt: string; by?: string }[];
}

interface Money {
  grossItemSales: number;
  itemDiscounts: number;
  orderDiscount: number;
  gst: number;
  pst: number;
  itemTax: number;
  delivery: number;
  tip: number;
  refunds?: number;
  total: number;
  adjustment: number;
}

interface PaymentLeg {
  legId: string;
  method: string;
  amount: number;
  status: string;
  reference?: string;
  amountRefunded?: number;
  capturedAt?: string;
  refunds?: { refundId?: string; amount: number; status: string; reason?: string; createdAt?: string }[];
}

interface DetailResponse {
  order: OrderDetail;
  transaction: { status?: string; totalPaid?: number; totalRefunded?: number; legs?: PaymentLeg[] } | null;
  hotelName: string | null;
  money: Money;
}

const SOURCE_LABELS: Record<string, string> = {
  online_delivery: "Online Delivery",
  online_pickup: "Online Pickup",
  kiosk: "Kiosk",
  counter: "Counter",
};

const METHOD_LABELS: Record<string, string> = {
  square: "Square (Card)",
  stripe: "Card",
  card: "Card",
  wallet: "Wallet",
  bonus_wallet: "Bonus Wallet",
  cash: "Cash",
  gift_card: "Gift Card",
  voucher: "Voucher",
  loyalty_points: "Loyalty Points",
};

const money = (n: unknown) => `${cs()}${(Number(n) || 0).toFixed(2)}`;
const when = (d?: string) => (d ? new Date(d).toLocaleString() : "—");
const titleCase = (s?: string) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const statusClass = (status?: string) => {
  const s = String(status || "").toLowerCase();
  if (s === "cancelled" || s === "failed" || s === "refunded") return "bg-red-100 text-red-700";
  if (["pickedup", "delivered", "completed", "ready"].includes(s)) return "bg-green-100 text-green-700";
  return "bg-blue-100 text-blue-700";
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="m-0 text-xs text-gray-500">{label}</p>
    <p className="m-0 text-sm font-medium text-gray-800">{value || "—"}</p>
  </div>
);

/** One order with everything about it, plus Refund / Compensate. */
const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isLocationAdmin = getTokenInfo()?.role === "location_admin";
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRefund, setShowRefund] = useState(false);
  const [showCompensate, setShowCompensate] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/admin/orders/detail/${id}`);
      setData(res.data?.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load order");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-5xl p-4 lg:p-8">
        <button onClick={() => navigate("/admin/orders")} className="mb-4 flex items-center gap-1 text-sm text-blue-600">
          <ArrowLeft size={16} /> Back to Orders
        </button>
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error || "Order not found"}</div>
      </div>
    );
  }

  const { order, transaction, hotelName, money: m } = data;
  const cancelled = order.orderStatus === "cancelled" || order.totalAmount === 0;
  const legs = transaction?.legs || [];

  return (
    <div className="mx-auto max-w-5xl p-4 pt-0 lg:p-8">
      {showRefund && (
        <RefundOrderModal order={order} onClose={() => setShowRefund(false)} onDone={() => load()} />
      )}
      {showCompensate && (
        <CompensateOrderModal
          order={order}
          restaurantPaysOnly={isLocationAdmin}
          onClose={() => setShowCompensate(false)}
          onDone={() => load()}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <button onClick={() => navigate("/admin/orders")} className="mb-3 flex items-center gap-1 text-sm text-blue-600">
          <ArrowLeft size={16} /> Back to Orders
        </button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-2xl font-bold text-gray-800">Order #{order.orderNumber}</h1>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusClass(order.orderStatus)}`}>
                {titleCase(order.orderStatus) || "—"}
              </span>
            </div>
            <p className="m-0 mt-1 text-sm text-gray-500">
              {when(order.createdAt)}
              {hotelName ? ` · ${hotelName}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="flex h-9 w-9 items-center justify-center rounded-full border text-gray-600 hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={() => setShowRefund(true)}
              disabled={cancelled}
              title={cancelled ? "Order already cancelled / refunded" : "Refund to original payment"}
              className="flex items-center gap-1.5 rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Undo2 size={16} /> Refund
            </button>
            <button
              type="button"
              onClick={() => setShowCompensate(true)}
              disabled={cancelled || !order.phoneNumber}
              title={
                !order.phoneNumber
                  ? "No customer phone number on this order"
                  : cancelled
                    ? "Order already cancelled / refunded"
                    : "Add compensation points"
              }
              className="flex items-center gap-1.5 rounded-md border border-purple-200 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={16} /> Compensate
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Order info */}
        <div className="grid grid-cols-2 gap-4 rounded-xl border bg-white p-4 sm:grid-cols-3 lg:grid-cols-4">
          <Row label="Total" value={cancelled ? <span className="text-red-600">Cancelled</span> : money(order.totalAmount)} />
          <Row label="Payment" value={METHOD_LABELS[order.payChoice || ""] || titleCase(order.payChoice)} />
          <Row label="Order Source" value={SOURCE_LABELS[order.orderSource || ""] || "—"} />
          <Row label="Order Type" value={order.choice} />
          <Row label="Customer Phone" value={order.phoneNumber} />
          <Row label="Placed By" value={order.orderBy === "employee" ? `Employee${order.employeeName ? ` (${order.employeeName})` : ""}` : "Customer"} />
          {order.tableToken ? <Row label="Table" value={order.tableToken} /> : null}
          <Row label="Invoice No." value={order.invoiceNumber} />
          {order.scheduledAt && <Row label="Scheduled For" value={when(order.scheduledAt)} />}
          {order.deliveryStatus && <Row label="Delivery Status" value={titleCase(order.deliveryStatus)} />}
          {getDeliveryAddress(order) && (
            <div className="col-span-2">
              <Row label="Delivery Address" value={getDeliveryAddress(order)} />
            </div>
          )}
          {order.deliveryLiveMapUrl && (
            <Row
              label="Delivery Tracking"
              value={
                <a href={order.deliveryLiveMapUrl} target="_blank" rel="noreferrer" className="text-blue-600">
                  Open live map
                </a>
              }
            />
          )}
        </div>

        {/* Items */}
        <div className="overflow-hidden rounded-xl border bg-white">
          <h2 className="m-0 bg-gray-100 px-4 py-3 text-base font-bold text-gray-700">Items</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr className="border-b">
                  <th className="p-3 text-left font-semibold">Item</th>
                  <th className="p-3 text-center font-semibold">Qty</th>
                  <th className="p-3 text-right font-semibold">Unit Price</th>
                  <th className="p-3 text-right font-semibold">Tax / unit</th>
                  <th className="p-3 text-right font-semibold">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, i) => {
                  const itemCancelled = item.itemstatus === "cancelled";
                  return (
                    <tr key={i} className={`border-b last:border-0 align-top ${itemCancelled ? "text-gray-400" : ""}`}>
                      <td className="p-3">
                        <p className={`m-0 font-medium ${itemCancelled ? "line-through" : "text-gray-800"}`}>
                          {item.name}
                          {itemCancelled && <span className="ml-2 text-[11px] text-red-600 no-underline">Cancelled</span>}
                          {!itemCancelled && (item.cancelledQuantity ?? 0) > 0 && (
                            <span className="ml-2 text-[11px] text-red-600">{item.cancelledQuantity} cancelled</span>
                          )}
                        </p>
                        {(item.customOptions || []).length > 0 && (
                          <ul className="m-0 mt-1 list-none p-0 text-xs text-gray-500">
                            {item.customOptions!.map((opt, j) => (
                              <li key={j}>
                                + {opt.label}
                                {opt.qty > 1 ? ` ×${opt.qty}` : ""}
                                {opt.price > 0 ? ` (${money(opt.price)})` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                        {item.dealName && (
                          <p className="m-0 mt-1 text-xs font-medium text-orange-700">🎁 Deal: {item.dealName}</p>
                        )}
                        {item.requestedCustomization && (
                          <p className="m-0 mt-1 text-xs italic text-gray-500">Note: {item.requestedCustomization}</p>
                        )}
                      </td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-right">{money(item.price)}</td>
                      <td className="p-3 text-right">{money(item.tax)}</td>
                      <td className="p-3 text-right font-medium">{money(item.price * item.quantity)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Money */}
          <div className="rounded-xl border bg-white p-4">
            <h2 className="m-0 mb-3 text-base font-bold text-gray-700">Amount Breakdown</h2>
            <dl className="m-0 space-y-1.5 text-sm">
              {[
                ["Item sales", m.grossItemSales],
                ["Item discounts", -m.itemDiscounts],
                ["Order discount", -m.orderDiscount],
                ["GST", m.gst],
                ["PST", m.pst],
                ...(m.gst || m.pst ? [] : [["Tax", m.itemTax]]),
                ["Delivery", m.delivery],
                ["Tip", m.tip],
                ["Partial refunds", -(m.refunds || 0)],
                ["Tax rounding / adjustment", m.adjustment],
              ]
                .filter(([, v]) => Math.abs(Number(v)) > 0.004)
                .map(([label, v]) => (
                  <div key={String(label)} className="flex justify-between">
                    <dt className="text-gray-600">{label}</dt>
                    <dd className="m-0 text-gray-800">
                      {Number(v) < 0 ? "-" : ""}
                      {money(Math.abs(Number(v)))}
                    </dd>
                  </div>
                ))}
              <div className="flex justify-between border-t pt-2 font-bold">
                <dt>Total</dt>
                <dd className="m-0">{money(m.total)}</dd>
              </div>
            </dl>
            {(order.partialRefunds || []).length > 0 && (
              <div className="mt-3 border-t pt-3">
                <p className="m-0 mb-1.5 text-xs font-semibold text-gray-600">Custom refunds</p>
                {order.partialRefunds!.map((r, i) => (
                  <div key={i} className="flex justify-between gap-3 text-xs text-gray-600">
                    <span>
                      {when(r.createdAt)}
                      {r.reason ? ` · ${r.reason}` : ""}
                    </span>
                    <span className="font-medium text-red-600">-{money(r.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status history */}
          <div className="rounded-xl border bg-white p-4">
            <h2 className="m-0 mb-3 text-base font-bold text-gray-700">Status History</h2>
            {(order.statusHistory || []).length === 0 ? (
              <p className="m-0 text-sm text-gray-500">No status changes recorded.</p>
            ) : (
              <ol className="m-0 list-none space-y-2 p-0">
                {order.statusHistory!.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className={`rounded px-2 py-0.5 text-xs ${statusClass(s.status)}`}>{titleCase(s.status)}</span>
                    <span className="text-xs text-gray-500">{when(s.updatedAt)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* Payments */}
        <div className="overflow-hidden rounded-xl border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-100 px-4 py-3">
            <h2 className="m-0 text-base font-bold text-gray-700">Payments</h2>
            {transaction && (
              <span className="text-xs text-gray-600">
                Paid {money(transaction.totalPaid)} · Refunded {money(transaction.totalRefunded)}
              </span>
            )}
          </div>
          {legs.length === 0 ? (
            <p className="m-0 px-4 py-3 text-sm text-gray-500">No payment transaction recorded for this order.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr className="border-b">
                    <th className="p-3 text-left font-semibold">Method</th>
                    <th className="p-3 text-right font-semibold">Amount</th>
                    <th className="p-3 text-left font-semibold">Status</th>
                    <th className="p-3 text-left font-semibold">Reference</th>
                    <th className="p-3 text-left font-semibold">Refunds</th>
                  </tr>
                </thead>
                <tbody>
                  {legs.map((leg) => (
                    <tr key={leg.legId} className="border-b align-top last:border-0">
                      <td className="p-3 font-medium text-gray-800">
                        {METHOD_LABELS[leg.method] || titleCase(leg.method)}
                        {leg.capturedAt && <span className="block text-[11px] font-normal text-gray-500">{when(leg.capturedAt)}</span>}
                      </td>
                      <td className="p-3 text-right">{money(leg.amount)}</td>
                      <td className="p-3">
                        <span className={`rounded px-2 py-0.5 text-xs ${statusClass(leg.status)}`}>{titleCase(leg.status)}</span>
                      </td>
                      <td className="p-3 break-all text-xs text-gray-500">{leg.reference || "—"}</td>
                      <td className="p-3 text-xs">
                        {(leg.refunds || []).length === 0 ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          leg.refunds!.map((r, i) => (
                            <div key={r.refundId || i} className="mb-1">
                              {money(r.amount)} · <span className="font-medium">{titleCase(r.status)}</span>
                              {r.reason && <span className="text-gray-500"> · {r.reason}</span>}
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
